/**
 * Menu Items Configuration
 * Defines navigation menu items for each user role
 */

import {
  IconGroup,
  IconDashboard,
  IconCourse,
  IconMessage,
  IconSchedule,
  IconRoom,
  IconUsers,
  IconMockExam,
  IconPracticeTest,
  IconAnnouncement,
  IconLogin,
  IconProfile,
  IconQuestionBank,
  IconAnalytics,
  IconAttendance,
  IconTuition,
  IconParentLink,
} from '../components/icons';

/**
 * Get menu items based on user role.
 * Resources / Ranking are intentionally NOT here – they live in the header
 * as gamification entry points across all roles.
 *
 * Each entry carries a `group` (`daily` | `academic` | `people` | `content` |
 * `account`) so the sidebar can render three or four scannable sections instead
 * of one flat run of thirteen rows (app-shell.md §2). It is a presentation
 * grouping only: paths, keys, icons and the order inside a group do not move,
 * and MobileNavConfig.flatten() ignores it, so the phone nav is unaffected.
 * `account` (profile) is pinned to the sidebar foot, not a nav section.
 *
 * @param {string} role - User role (ADMIN, TEACHER, STUDENT)
 * @param {Function} t - Translation function from useTranslation hook
 * @returns {Array} Menu items array
 */
export const getMenuItems = (role, t) => {
  switch (role) {
    case 'ADMIN':
      return [
        {
          key: 1,
          icon: <IconDashboard />,
          label: t('navigation.dashboard'),
          path: '/admin/dashboard',
          group: 'daily',
        },
        // High up on purpose: marking the register is the one thing reception
        // does every single day, several times a day.
        {
          key: 11,
          icon: <IconAttendance />,
          label: t('navigation.attendance'),
          path: '/admin/attendance',
          group: 'daily',
        },
        // Second only to attendance: the debtor list is the screen an owner
        // opens the app for, and the plans page hangs off it rather than
        // occupying a slot of its own.
        // One destination, no children: tuition plans were removed because the
        // centre prices a course once and every group of it charges that. The
        // monthly amount comes from `courses.price`, edited where courses are.
        {
          key: 12,
          icon: <IconTuition />,
          label: t('navigation.tuition'),
          path: '/admin/tuition',
          group: 'daily',
        },
        {
          key: 2,
          icon: <IconCourse />,
          label: t('navigation.courses'),
          path: '/admin/courses',
          group: 'academic',
        },
        {
          key: 3,
          icon: <IconGroup />,
          label: t('navigation.groups'),
          path: '/admin/groups',
          group: 'academic',
        },
        {
          key: 4,
          icon: <IconSchedule />,
          label: t('navigation.schedule'),
          path: '/admin/lesson-schedules/time-table',
          group: 'academic',
        },
        {
          key: 5,
          icon: <IconUsers />,
          label: t('navigation.users'),
          path: '/admin/users',
          group: 'people',
        },
        // Its own entry rather than a tab under Users: the bot delivers nothing
        // until a parent has tapped their link, so "how many are connected" is a
        // number somebody has to be able to see without going looking for it.
        {
          key: 13,
          icon: <IconParentLink />,
          label: t('navigation.parentLinks'),
          path: '/admin/parent-links',
          group: 'people',
        },
        {
          key: 6,
          icon: <IconAnnouncement />,
          label: t('navigation.announcements'),
          path: '/admin/updates',
          group: 'people',
        },
        {
          key: 7,
          icon: <IconRoom />,
          label: t('navigation.rooms'),
          path: '/admin/rooms',
          group: 'academic',
        },
        {
          key: 8,
          icon: <IconMockExam />,
          label: t('navigation.mockExams'),
          path: '/admin/mock-exams',
          group: 'academic',
        },
        {
          key: 9,
          icon: <IconProfile />,
          label: t('navigation.profile'),
          path: '/admin/profile',
          group: 'account',
        },
        {
          key: 10,
          icon: <IconMessage />,
          label: t('navigation.messages'),
          path: '/admin/messages',
          group: 'daily',
        },
      ];

    case 'TEACHER':
      return [
        {
          key: 1,
          icon: <IconDashboard />,
          label: t('navigation.dashboard'),
          path: '/teacher/dashboard',
          group: 'daily',
        },
        {
          key: 2,
          icon: <IconGroup />,
          label: t('navigation.groups'),
          path: '/teacher/groups',
          group: 'academic',
        },
        {
          key: 3,
          icon: <IconSchedule />,
          label: t('navigation.schedule'),
          path: '/teacher/lessons',
          group: 'daily',
        },
        // One destination: the explorer reaches every set, and its "All sets"
        // mode folds in the flat cross-course register the second entry used to
        // be (W6.1). One glyph, one concept, one door.
        {
          key: 4,
          icon: <IconQuestionBank />,
          label: t('navigation.questionBank'),
          path: '/teacher/question-bank',
          group: 'content',
        },
        {
          key: 6,
          icon: <IconPracticeTest />,
          label: t('practiceTests.title'),
          path: '/teacher/practice-tests',
          group: 'academic',
        },
        {
          key: 7,
          icon: <IconAnalytics />,
          label: t('analytics.title'),
          path: '/teacher/analytics',
          group: 'academic',
        },
        {
          key: 8,
          icon: <IconProfile />,
          label: t('navigation.profile'),
          path: '/teacher/profile',
          group: 'account',
        },
        {
          key: 9,
          icon: <IconMessage />,
          label: t('navigation.messages'),
          path: '/teacher/messages',
          group: 'daily',
        },
      ];

    case 'STUDENT':
      return [
        {
          key: 1,
          icon: <IconDashboard />,
          label: t('navigation.dashboard'),
          path: '/student/dashboard',
          group: 'daily',
        },
        {
          key: 2,
          icon: <IconCourse />,
          label: t('navigation.courses'),
          path: '/student/subjects',
          group: 'academic',
        },
        {
          key: 3,
          icon: <IconSchedule />,
          label: t('navigation.schedule'),
          path: '/student/lessons',
          group: 'daily',
        },
        {
          key: 4,
          icon: <IconPracticeTest />,
          label: t('practiceTests.title'),
          path: '/student/practice-tests',
          group: 'academic',
        },
        {
          key: 6,
          icon: <IconAnalytics />,
          label: t('analytics.myPerformance'),
          path: '/student/performance',
          group: 'academic',
        },
        // No balance entry here on purpose. A student checks what they owe when
        // a parent asks — a few times a term — and a permanent nav slot for it
        // pushed something they use daily into the "More" sheet on mobile. It
        // lives on the profile screen instead, where the rest of "about me" is.
        {
          key: 7,
          icon: <IconProfile />,
          label: t('navigation.profile'),
          path: '/student/profile',
          group: 'account',
        },
        {
          key: 8,
          icon: <IconMessage />,
          label: t('navigation.messages'),
          path: '/student/messages',
          group: 'daily',
        },
      ];

    default:
      return [
        {
          key: 1,
          icon: <IconLogin />,
          label: t('auth.login'),
          path: '/login',
          group: 'daily',
        },
      ];
  }
};

export default getMenuItems;
